/**
 * The four Zuordnungs-Dialoge — Inventar 2.0, S7a.
 *
 * MOVED OUT OF THE PAGE, NOT REWRITTEN. Fields, order, attributes, guards,
 * payload-shaping and reload behaviour all stay with the page: every handler
 * arrives as a prop and nothing here calls the API. What changes is the
 * chrome — one shared shell (see InventoryDialog.jsx) and Vantio tokens
 * instead of four hand-copied hex palettes.
 *
 * DELIBERATE ASYMMETRIES KEPT AS THEY ARE (all four pinned by S6a):
 *  - CREATE preselects the first unit, MOVE preselects nothing.
 *  - CREATE's Menge has `max={available}`, EDIT's has none — their ceilings
 *    are different formulas and are enforced in the page's submit handlers.
 *  - MOVE's submit is disabled while no target unit is chosen; CREATE's is not.
 *  - REMOVE has no <form>: its confirm is a type="button" with onClick, so
 *    Enter does not submit and there is no browser-level required check.
 *  - CREATE says „Room", MOVE says „Raum". Not harmonised — that is visible
 *    copy, and changing it is a decision, not a restyle.
 *
 * The item-edit and Schaden-melden dialogs are NOT in this file. They belong
 * to S6b's and S7b's surfaces respectively and still carry the legacy hex
 * chrome; until S7b lands the page therefore has two dialog styles.
 */
import React from "react";

import { normalizeUnit, formatUnitSelectOptionLabel } from "../../../api/adminData";
import { getUnitHeadingLabel } from "../../../utils/unitDisplayId";
import {
  DIALOG_BTN_DESTRUCTIVE,
  DIALOG_BTN_PRIMARY,
  DIALOG_BTN_SECONDARY,
  DIALOG_CONTROL,
  DialogActions,
  DialogError,
  DialogField,
  DialogHint,
  InventoryDialog,
} from "./InventoryDialog";
import { UnitAssignSearchHitList } from "./InventoryUnitSearch";

/** Muted helper line inside a field — reset to normal case, the label is not. */
const FIELD_NOTE =
  "m-0 mb-2 text-[11px] font-medium normal-case leading-snug tracking-normal text-muted-foreground";

function UnitOptions({ units }) {
  return (
    <>
      <option value="">— wählen —</option>
      {units.map((u) => {
        const nu = normalizeUnit(u);
        const id = nu.id || nu.unitId;
        return (
          <option key={id} value={id}>
            {formatUnitSelectOptionLabel(u)}
          </option>
        );
      })}
    </>
  );
}

function RoomOptions({ rooms }) {
  return (
    <>
      <option value="">— ganze Unit —</option>
      {rooms.map((r) => {
        const rid = r.id || r.room_id;
        return (
          <option key={rid} value={rid}>
            {r.name || r.roomName || rid}
          </option>
        );
      })}
    </>
  );
}

export function InventoryAssignCreateDialog({
  error,
  form,
  onChangeForm,
  rooms,
  saving,
  unitFilter,
  onChangeUnitFilter,
  filteredUnits,
  available,
  onSubmit,
  onCancel,
}) {
  return (
    <InventoryDialog title="Zuordnung hinzufügen">
      {error ? <DialogError>{error}</DialogError> : null}
      <form onSubmit={onSubmit} className="mt-4 grid gap-3">
        <DialogField label="Unit *">
          <input
            type="search"
            value={unitFilter}
            onChange={(e) => onChangeUnitFilter(e.target.value)}
            placeholder="Nach Unit-ID oder Adresse suchen…"
            autoComplete="off"
            className={`${DIALOG_CONTROL} mb-2`}
          />
          <UnitAssignSearchHitList
            query={unitFilter}
            units={filteredUnits}
            selectedUnitId={form.unit_id}
            onSelectUnitId={(id) => onChangeForm((f) => ({ ...f, unit_id: id, room_id: "" }))}
          />
          {unitFilter.trim() && filteredUnits.length === 0 ? (
            <p className={FIELD_NOTE}>Keine passende Unit gefunden</p>
          ) : null}
          <select
            required
            value={form.unit_id}
            onChange={(e) => onChangeForm((f) => ({ ...f, unit_id: e.target.value, room_id: "" }))}
            className={DIALOG_CONTROL}
          >
            <UnitOptions units={filteredUnits} />
          </select>
        </DialogField>

        <DialogField label="Room (optional)">
          <select
            value={form.room_id}
            onChange={(e) => onChangeForm((f) => ({ ...f, room_id: e.target.value }))}
            className={DIALOG_CONTROL}
          >
            <RoomOptions rooms={rooms} />
          </select>
        </DialogField>

        <DialogField label={`Menge (frei: ${available})`}>
          <input
            type="number"
            min={1}
            max={available}
            value={form.quantity}
            onChange={(e) => onChangeForm((f) => ({ ...f, quantity: e.target.value }))}
            className={DIALOG_CONTROL}
          />
        </DialogField>

        <DialogField label="Notiz">
          <input
            value={form.notes}
            onChange={(e) => onChangeForm((f) => ({ ...f, notes: e.target.value }))}
            className={DIALOG_CONTROL}
          />
        </DialogField>

        <DialogActions>
          <button type="button" onClick={onCancel} className={DIALOG_BTN_SECONDARY}>
            Abbrechen
          </button>
          <button type="submit" disabled={saving} className={DIALOG_BTN_PRIMARY}>
            {saving ? "…" : "Zuordnen"}
          </button>
        </DialogActions>
      </form>
    </InventoryDialog>
  );
}

export function InventoryAssignEditDialog({ error, form, onChangeForm, saving, onSubmit, onCancel }) {
  return (
    <InventoryDialog title="Zuordnung bearbeiten">
      {error ? <DialogError>{error}</DialogError> : null}
      <DialogHint>Standort (Wohnung / Raum) über „Verschieben" ändern.</DialogHint>
      <form onSubmit={onSubmit} className="mt-4 grid gap-3">
        <DialogField label="Menge">
          <input
            type="number"
            min={1}
            value={form.quantity}
            onChange={(e) => onChangeForm((f) => ({ ...f, quantity: e.target.value }))}
            className={DIALOG_CONTROL}
          />
        </DialogField>

        <DialogField label="Notiz">
          <input
            value={form.notes}
            onChange={(e) => onChangeForm((f) => ({ ...f, notes: e.target.value }))}
            className={DIALOG_CONTROL}
          />
        </DialogField>

        <DialogActions>
          <button type="button" onClick={onCancel} className={DIALOG_BTN_SECONDARY}>
            Abbrechen
          </button>
          <button type="submit" disabled={saving} className={DIALOG_BTN_PRIMARY}>
            {saving ? "…" : "Speichern"}
          </button>
        </DialogActions>
      </form>
    </InventoryDialog>
  );
}

export function InventoryAssignRemoveDialog({ error, reason, onChangeReason, saving, onConfirm, onCancel }) {
  return (
    <InventoryDialog title="Zuordnung entfernen">
      <DialogHint>
        Die Zuordnung wird beendet. Inspektions- und Schadenshistorie bleibt erhalten.
      </DialogHint>
      {error ? <DialogError>{error}</DialogError> : null}
      <DialogField label="Grund (optional)" className="mt-4">
        <textarea
          rows={2}
          value={reason}
          onChange={(e) => onChangeReason(e.target.value)}
          className={`${DIALOG_CONTROL} resize-y`}
        />
      </DialogField>
      <DialogActions>
        <button type="button" onClick={onCancel} className={DIALOG_BTN_SECONDARY}>
          Abbrechen
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={onConfirm}
          className={DIALOG_BTN_DESTRUCTIVE}
        >
          {saving ? "…" : "Entfernen"}
        </button>
      </DialogActions>
    </InventoryDialog>
  );
}

export function InventoryAssignMoveDialog({
  row,
  unitById,
  error,
  form,
  onChangeForm,
  rooms,
  saving,
  unitFilter,
  onChangeUnitFilter,
  filteredUnits,
  reason,
  onChangeReason,
  onSubmit,
  onCancel,
}) {
  const u = unitById.get(String(row.unit_id));
  const nu = u ? normalizeUnit(u) : null;
  const ul = nu ? getUnitHeadingLabel(nu) : row.unit_id;
  const current = row.room_name ? `${ul} · ${row.room_name}` : ul;

  return (
    <InventoryDialog title="Zuordnung verschieben">
      <DialogHint>Aktuell: {current}</DialogHint>
      {error ? <DialogError>{error}</DialogError> : null}
      <form onSubmit={onSubmit} className="mt-4 grid gap-3">
        <DialogField label="Zielwohnung *">
          <input
            type="search"
            value={unitFilter}
            onChange={(e) => onChangeUnitFilter(e.target.value)}
            placeholder="Nach Unit-ID oder Adresse suchen…"
            autoComplete="off"
            className={`${DIALOG_CONTROL} mb-2`}
          />
          <UnitAssignSearchHitList
            query={unitFilter}
            units={filteredUnits}
            selectedUnitId={form.unit_id}
            onSelectUnitId={(id) => onChangeForm((f) => ({ ...f, unit_id: id, room_id: "" }))}
          />
          {unitFilter.trim() && filteredUnits.length === 0 ? (
            <p className={FIELD_NOTE}>Keine passende Unit gefunden</p>
          ) : null}
          <select
            required
            value={form.unit_id}
            onChange={(e) => onChangeForm((f) => ({ ...f, unit_id: e.target.value, room_id: "" }))}
            className={DIALOG_CONTROL}
          >
            <UnitOptions units={filteredUnits} />
          </select>
        </DialogField>

        <DialogField label="Raum (optional)">
          <select
            value={form.room_id}
            onChange={(e) => onChangeForm((f) => ({ ...f, room_id: e.target.value }))}
            className={DIALOG_CONTROL}
          >
            <RoomOptions rooms={rooms} />
          </select>
        </DialogField>

        <DialogField label="Grund (optional)">
          <input
            value={reason}
            onChange={(e) => onChangeReason(e.target.value)}
            className={DIALOG_CONTROL}
          />
        </DialogField>

        <DialogActions>
          <button type="button" onClick={onCancel} className={DIALOG_BTN_SECONDARY}>
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={saving || !form.unit_id}
            className={DIALOG_BTN_PRIMARY}
          >
            {saving ? "…" : "Verschieben"}
          </button>
        </DialogActions>
      </form>
    </InventoryDialog>
  );
}
